import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RotateCcw, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  ReasonDialog,
  SearchBox,
  apiErrorMessage,
} from "@/components/admin/AdminUI";

/**
 * Lixeira de ocorrências: excluídas no escopo do usuário, com autor, data e
 * justificativa; restauração exige `ocorrencias.restore` e é auditada.
 */
export default function OccurrenceTrash() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState("all");
  const [unitId, setUnitId] = useState("all");
  const [units, setUnits] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [clients, setClients] = useState([]);
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(() => {
    const params = { page, limit: 20 };
    if (search) params.search = search;
    if (clientId !== "all") params.client_id = clientId;
    if (unitId !== "all") params.unit_id = unitId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/occurrences-trash", { params })
      .then(({ data: payload }) => setData(payload))
      .catch((err) => {
        setData({ items: [], total: 0, pages: 1 });
        toast.error(apiErrorMessage(err));
      });
  }, [page, search, clientId, unitId, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, clientId, unitId, dateFrom, dateTo]);
  // Unidades da empresa escolhida (ou da única empresa do usuário).
  const unitClient = clientId !== "all" ? clientId : clients.length === 1 ? clients[0].id : null;
  useEffect(() => {
    setUnitId("all");
    if (!unitClient) { setUnits([]); return; }
    api.get("/units", { params: { client_id: unitClient } })
      .then(({ data: list }) => setUnits(Array.isArray(list) ? list : list.items || []))
      .catch(() => setUnits([]));
  }, [unitClient]);
  useEffect(() => {
    api.get("/clients").then(({ data: list }) => setClients(Array.isArray(list) ? list : list.items || [])).catch(() => {});
  }, []);

  const restore = async (reason) => {
    try {
      await api.post(`/occurrences/${restoring.occurrence_id}/restore`, { reason });
      toast.success(`Ocorrência ${restoring.protocol || ""} restaurada.`);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
      throw err;
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5" data-testid="occurrence-trash">
      <PageHeader
        icon={Trash2}
        title="Lixeira de ocorrências"
        description={data ? `${data.total} ocorrência(s) excluída(s) no seu escopo. O documento completo é preservado e pode ser restaurado.` : ""}
        actions={<Button asChild variant="outline" size="sm"><Link to="/ocorrencias">Voltar às ocorrências</Link></Button>}
      />
      <div className="flex flex-col flex-wrap gap-2 sm:flex-row">
        <SearchBox value={search} onChange={setSearch} placeholder="Protocolo, justificativa ou autor" testId="trash-search" />
        {clients.length > 1 && (
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="sm:w-52" aria-label="Filtrar por empresa"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as empresas</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {units.length > 1 && (
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger className="sm:w-48" aria-label="Filtrar por unidade" data-testid="trash-unit-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as unidades</SelectItem>
              {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="sm:w-40" aria-label="Excluídas a partir de" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="sm:w-40" aria-label="Excluídas até" />
      </div>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {data === null ? <LoadingState /> : data.items.length === 0 ? (
          <EmptyState title="A lixeira está vazia" description="Ocorrências excluídas no seu escopo aparecem aqui." testId="trash-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trash-table">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Ocorrência</th>
                  <th className="px-4 py-3 font-medium">Empresa / unidade</th>
                  <th className="px-4 py-3 font-medium">Excluída em</th>
                  <th className="px-4 py-3 font-medium">Justificativa</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.occurrence_id} className="border-b border-border/60 align-top last:border-0" data-testid={`trash-row-${item.occurrence_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{item.protocol || item.occurrence_id.slice(0, 8)}</p>
                      <p className="text-xs text-muted-foreground">{[item.category, item.priority].filter(Boolean).join(" · ")}</p>
                    </td>
                    <td className="px-4 py-3 text-xs">{item.client_name || "—"}<br /><span className="text-muted-foreground">{item.unit_name || ""}</span></td>
                    <td className="px-4 py-3 text-xs">{formatDate(item.deleted_at)}<br /><span className="text-muted-foreground">por {item.deleted_by_name || "—"}</span></td>
                    <td className="max-w-xs px-4 py-3 text-xs">{item.reason || <span className="text-muted-foreground">não informada</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => setRestoring(item)} data-testid={`trash-restore-${item.occurrence_id}`}>
                        <RotateCcw size={13} className="mr-1" /> Restaurar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {data && data.pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Página anterior"><ChevronLeft size={15} /></Button>
          <span>{page} / {data.pages}</span>
          <Button variant="outline" size="icon" disabled={page >= data.pages} onClick={() => setPage(page + 1)} aria-label="Próxima página"><ChevronRight size={15} /></Button>
        </div>
      )}
      <ReasonDialog
        open={Boolean(restoring)}
        onOpenChange={(open) => !open && setRestoring(null)}
        title={`Restaurar ${restoring?.protocol || "ocorrência"}`}
        description="A ocorrência volta às listas, indicadores e relatórios com todo o histórico. A restauração é auditada."
        confirmLabel="Restaurar"
        onConfirm={restore}
        testId="trash-restore-dialog"
      />
    </div>
  );
}
